import { RTCSession } from '@wui/jssip/lib/RTCSession'
import { ShallowRef, triggerRef } from 'vue'

import { useStoreMediaDevices } from '@/entities/media-devices'
import { DeviceId } from '@/entities/media-devices/types'

import { useUserMediaStream } from '@/shared/composables/media-stream'
import { getReinviteOptions } from '@/shared/utils/jssip'
import { applyScreenShareSenderParams, applyScreenShareSenderParamsWithRetry } from '@/shared/utils/webrtc'

type FindReusableVideoSenderOptions = {
  /**
   * When true, only reuse the sender that currently holds preferredTrackId.
   * Required for conference screen share: cam and screen are separate m-lines;
   * falling back to a muted/empty webcam slot would publish screen on the webcam stream.
   */
  onlyPreferredTrack?: boolean
}

/**
 * Finds an existing video RTCRtpSender to replaceTrack into.
 * Prefer preferredTrackId, then ended/disabled video tracks, then empty own publish slots
 * (sendrecv/sendonly). Skips recvonly remote inbound m-lines so conference joiners fall
 * through to trackForAdd + jssip addTrack on re-INVITE.
 */
export const findReusableVideoSender = (
  connection: RTCPeerConnection,
  preferredTrackId?: string,
  options?: FindReusableVideoSenderOptions,
): RTCRtpSender | undefined => {
  const senders = connection.getSenders()

  if (preferredTrackId) {
    const byPreferredId = senders.find((sender) => sender.track?.id === preferredTrackId)
    if (byPreferredId) {
      return byPreferredId
    }
  }

  if (options?.onlyPreferredTrack) {
    return undefined
  }

  const endedOrDisabledVideoSender = senders.find((sender) => {
    const track = sender.track
    return !!track && track.kind === 'video' && (track.readyState === 'ended' || !track.enabled)
  })
  if (endedOrDisabledVideoSender) {
    return endedOrDisabledVideoSender
  }

  const transceivers = connection.getTransceivers?.() ?? []
  for (const transceiver of transceivers) {
    const sender = transceiver.sender
    if (!sender || sender.track) {
      continue
    }

    // Remote inbound video m-lines are recvonly with an empty sender. Reusing them
    // via replaceTrack keeps direction recvonly (no outbound RTP) and skips trackForAdd,
    // so later joiners preview locally but never publish. Only own publish slots
    // (sendrecv/sendonly after detach) are safe to reuse.
    const direction = transceiver.direction
    if (direction !== 'sendrecv' && direction !== 'sendonly') {
      continue
    }

    const receiverKind = transceiver.receiver?.track?.kind
    const mid = transceiver.mid ?? ''
    const looksLikeVideo = receiverKind === 'video' || /(?:^|-)v(?:ideo)?(?:-|$)/i.test(mid) || mid.includes('-v')
    if (looksLikeVideo) {
      return sender
    }
  }

  return undefined
}

const detachVideoTrackFromPeerConnection = (
  peerConnection: RTCPeerConnection | undefined,
  videoTrack: MediaStreamTrack | undefined,
) => {
  if (!peerConnection || !videoTrack) {
    return
  }

  const sender = peerConnection.getSenders().find((item) => item.track?.id === videoTrack.id)
    ?? peerConnection.getSenders().find((item) => item.track?.kind === 'video' && item.track === videoTrack)

  if (!sender) {
    return
  }

  void sender.replaceTrack(null).catch((error) => {
    console.warn('[local-media] Failed to detach video track from sender', error)
  })
}

export const activateUserMediaStream = async (
  session: RTCSession,
  streamRef: ShallowRef<MediaStream | undefined>,
  type: 'video' | 'screensharing',
  trackId?: string,
) => {
  try {
    const stream = type === 'video' ? await useUserMediaStream().getUserMedia(false, true) : await useStoreMediaDevices().openDisplay()
    const videoTrack = stream.getVideoTracks()[0]
    // Conference screen share must not reuse a muted/empty webcam sender — that publishes
    // display media on the webcam m-line while IM announces SCREEN_SHARING_STREAM_NAME.
    const sender = findReusableVideoSender(
      session.connection,
      trackId,
      type === 'screensharing' ? { onlyPreferredTrack: true } : undefined,
    )

    if (sender) {
      await sender.replaceTrack(videoTrack ?? null)
      if (type === 'screensharing' && videoTrack) {
        await applyScreenShareSenderParams(sender)
      }
    } else {
      session.data = {
        trackForAdd: videoTrack,
      }
      if (type === 'screensharing' && videoTrack) {
        void applyScreenShareSenderParamsWithRetry(session.connection, videoTrack.id)
      }
    }

    streamRef.value = stream
    triggerRef(streamRef)

    return videoTrack
  } catch (error) {
    return Promise.reject(error)
  }
}

export const activateLocalScreenSharingAtPrivateSession = async (session: RTCSession, streamRef: ShallowRef<MediaStream | undefined>): Promise<boolean> => {
  try {
    const stream = await useStoreMediaDevices().openDisplay()
    const videoTrack = stream.getVideoTracks()[0]
    const sender = findReusableVideoSender(session.connection)
      ?? session.connection.getSenders().find((item) => item.track?.kind === 'video' && item.track.enabled)
    let result = Promise.resolve(true)
    if (sender) {
      await sender.replaceTrack(videoTrack ?? null)
      if (videoTrack) {
        await applyScreenShareSenderParams(sender)
      }
      streamRef.value = stream
      triggerRef(streamRef)
    } else if (videoTrack) {
      const newSender = session.connection.addTrack(videoTrack, stream)
      result = new Promise((resolve, reject) => {
        // WUI-3372: Явно указываем true для обоих параметров, чтобы правильно сформировать SDP offer
        session.renegotiate(getReinviteOptions(true, true), async () => {
          await applyScreenShareSenderParams(newSender)
          streamRef.value = stream
          triggerRef(streamRef)
          resolve(true)
        }, () => {
          const tracks = stream.getTracks()
          session.connection.removeTrack(newSender)
          tracks.forEach((track) => {
            track.stop()
          })
          reject(new Error('calls.renegotiationFailedVideo'))
        })
      })
    } else {
      result = Promise.reject(new Error('Screen sharing stream has no video track'))
    }
    return result
  } catch (error) {
    return Promise.reject(error)
  }
}

export const activateLocalCameraStreamAtPrivateSession = async (session: RTCSession, streamRef: ShallowRef<MediaStream | undefined>) => {
  try {
    const stream = await useUserMediaStream().getUserMedia(false, true)
    const videoTrack = stream.getVideoTracks()[0]
    const sender = findReusableVideoSender(session.connection)
      ?? session.connection.getSenders().find((item) => item.track?.kind === 'video')
    let result = Promise.resolve(true)
    if (sender) {
      await sender.replaceTrack(videoTrack ?? null)
      streamRef.value = stream
      triggerRef(streamRef)
    } else if (videoTrack) {
      const newSender = session.connection.addTrack(videoTrack, stream)
      result = new Promise((resolve, reject) => {
        // WUI-3371: Явно указываем true для обоих параметров, чтобы правильно сформировать SDP offer
        session.renegotiate(getReinviteOptions(true, true), () => {
          streamRef.value = stream
          // Не обновляем статус удаленного участника - он обновится когда придет DTMF/reinvite от него
          triggerRef(streamRef)
          resolve(true)
        }, () => {
          const tracks = stream.getTracks()
          session.connection.removeTrack(newSender)
          tracks.forEach((track) => {
            track.stop()
          })
          reject(new Error('calls.renegotiationFailedVideo'))
        })
      })
    } else {
      result = Promise.reject(new Error('Camera stream has no video track'))
    }
    return result
  } catch (error) {
    return Promise.reject(error)
  }
}

export const deactivateLocalVideoStream = (
  streamRef: ShallowRef<MediaStream | undefined>,
  peerConnection?: RTCPeerConnection,
) => {
  if (!streamRef.value) {
    return
  }

  const videoTrack = streamRef.value.getVideoTracks()[0]
  detachVideoTrackFromPeerConnection(peerConnection, videoTrack)
  videoTrack?.stop()
  streamRef.value = undefined
  triggerRef(streamRef)
}

export const deactivateLocalAudioStream = (streamRef: ShallowRef<MediaStream | undefined>) => {
  const stream = streamRef.value
  if (!stream) {
    return
  }

  useStoreMediaDevices().closeStream(stream)
  streamRef.value = undefined
  triggerRef(streamRef)
}

export const activateLocalAudioStream = async (
  session: RTCSession,
  streamRef: ShallowRef<MediaStream | undefined>,
  deviceId?: DeviceId,
): Promise<MediaStreamTrack | undefined> => {
  try {
    const storeMediaDevices = useStoreMediaDevices()
    const stream = await storeMediaDevices.openMicrophone(deviceId)
    const audioTrack = stream.getAudioTracks()[0]
    const connection = session.connection

    if (connection && connection.connectionState !== 'closed' && audioTrack) {
      const sender = connection.getSenders().find(({ track }) => track?.kind === 'audio')
      if (sender) {
        await sender.replaceTrack(audioTrack)
      }
    }

    streamRef.value = stream
    triggerRef(streamRef)

    return audioTrack
  } catch (error) {
    return Promise.reject(error)
  }
}

export const closePictureInPicture = (): void => {
  if (document.pictureInPictureElement) {
    document.exitPictureInPicture()
  }
}

export const openPictureInPicture = (querySelector = '#myStream') => {
  const video: HTMLVideoElement | null = document.querySelector<HTMLVideoElement>(querySelector)
  try {
    video?.requestPictureInPicture()
  } catch {
  }
}
